import React, { useEffect, useState } from "react";
import { listClasses, listSubjects, listStaff, getTimetableRun, getClassSubjectsByWeek, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Thống kê dạy thay: so từng ô trong grid (GV thực dạy) với GV CHÍNH theo
// PCCM (assignedTeacherId) của đúng (lớp, môn) đó — khác nhau thì tính là 1
// tiết dạy thay. "Dự phòng" (poolTeacherIds đã đăng ký sẵn) và "Ngoài kế
// hoạch" (GV không hề có trong PCCM lớp-môn đó, do bước lấp trống/dạy thay
// tự động chọn) tách riêng để phân biệt mức độ bất thường.
const HappySchoolSubstituteStats = ({ runId, weekId }) => {
  const schoolId = getActiveSchoolId();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true);
    setError("");
    Promise.all([
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }),
      getTimetableRun(schoolId, runId),
      // PCCM ĐÚNG CỦA TUẦN NÀY (PCCM chung + phần đổi riêng tuần — "Phân công chuyên môn (riêng tuần này)").
      weekId ? getClassSubjectsByWeek(schoolId, weekId).catch(() => null) : Promise.resolve(null),
    ])
      .then(([classes, subjects, staff, run, pccmWeek]) => {
        const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
        const subjectById = Object.fromEntries(subjects.map((s) => [s.id, s]));
        const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
        const grid = (run && run.grid) || {};

        // (classId,subjectId) -> {assignedTeacherId, poolTeacherIds}
        const pccmOf = {};
        classes.forEach((c) => {
          (c.subjects || []).forEach((s) => {
            pccmOf[`${c.id}|${s.subjectId}`] = s;
          });
        });
        // Ghi đè bằng PCCM của đúng tuần đang xem (nếu có).
        const eff = (pccmWeek && pccmWeek.effective) || {};
        Object.entries(eff).forEach(([cid, m]) => Object.entries(m || {}).forEach(([sid, r]) => {
          if (r) pccmOf[`${cid}|${sid}`] = { ...(pccmOf[`${cid}|${sid}`] || {}), ...r, subjectId: sid };
        }));

        // key = teacherId(dạy thay)|originalTeacherId|classId|subjectId
        const agg = {};
        Object.entries(grid).forEach(([key, val]) => {
          if (!val || val.length < 2 || !val[1]) return; // bỏ overlay không có GV (Nghỉ, HN...)
          const [classId, , ] = key.split("|");
          const [subjectId, teacherId] = val;
          const cfg = pccmOf[`${classId}|${subjectId}`];
          if (!cfg || !cfg.assignedTeacherId) return;
          if (cfg.assignedTeacherId === teacherId) return; // đúng GV chính, không phải dạy thay
          const k = `${teacherId}|${cfg.assignedTeacherId}|${classId}|${subjectId}`;
          agg[k] = agg[k] || {
            teacherId, originalTeacherId: cfg.assignedTeacherId, classId, subjectId, count: 0,
          };
          agg[k].count++;
        });

        const out = Object.values(agg).map((r) => ({
          ...r,
          teacherName: staffById[r.teacherId]?.name || r.teacherId,
          originalName: staffById[r.originalTeacherId]?.name || r.originalTeacherId,
          className: classById[r.classId]?.name || r.classId,
          subjectName: subjectById[r.subjectId]?.name || r.subjectId,
        }));
        out.sort((a, b) => b.count - a.count || a.teacherName.localeCompare(b.teacherName, "vi"));
        setRows(out);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId]);

  if (!runId) return <p style={{ color: "#6b7280" }}>Chưa chọn bản xếp TKB.</p>;
  if (loading || rows === null) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;

  const totalPeriods = rows.reduce((s, r) => s + r.count, 0);

  const byTeacher = {};
  rows.forEach((r) => {
    byTeacher[r.teacherId] = byTeacher[r.teacherId] || { name: r.teacherName, count: 0 };
    byTeacher[r.teacherId].count += r.count;
  });
  const teacherTotals = Object.values(byTeacher).sort((a, b) => b.count - a.count);

  const shown = rows;

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Mỗi ô trong bản TKB mà <b>GV thực dạy khác GV chính theo PCCM của tuần này</b> (PCCM chung + phần đổi riêng tuần) của đúng (lớp, môn) đó — tính là 1 tiết dạy
        thay. Không có GV dự phòng: ai không được PCCM lớp-môn đó mà dạy thì đều là dạy thay.
      </p>
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={statBox}>Tổng tiết dạy thay: <b>{totalPeriods}</b></div>
      </div>

      {teacherTotals.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13 }}>Tổng tiết dạy thay theo GV</h4>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {teacherTotals.map((t) => (
              <div key={t.name} style={{ ...statBox, padding: "5px 10px" }}>{t.name}: <b>{t.count}</b></div>
            ))}
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>GV dạy thay</th>
              <th style={{ ...th, textAlign: "left" }}>Dạy thay cho</th>
              <th style={th}>Lớp</th>
              <th style={{ ...th, textAlign: "left" }}>Môn</th>
              <th style={th}>Số tiết</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={5} style={{ ...td, color: "#6b7280", padding: 16 }}>Không có tiết dạy thay nào.</td></tr>
            ) : shown.map((r) => (
              <tr key={`${r.teacherId}|${r.originalTeacherId}|${r.classId}|${r.subjectId}`}>
                <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.teacherName}</td>
                <td style={{ ...td, textAlign: "left" }}>{r.originalName}</td>
                <td style={td}>{r.className}</td>
                <td style={{ ...td, textAlign: "left" }}>{r.subjectName}</td>
                <td style={{ ...td, fontWeight: 700 }}>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const statBox = { padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", fontSize: 13 };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };

export default HappySchoolSubstituteStats;
